import React, { useEffect, useState, useContext } from 'react';
import { Link, Navigate } from 'react-router-dom';
import Select from 'react-select';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AppSettings } from '../../config/app-settings.js';
import { z } from 'zod';
import { saveUser } from './../../services/users.js';
import { getUserRoles as getRoles } from './../../services/userroles.js';

// Zod schema: note that we don't include the avatar file.
const schema = z
  .object({
    firstName: z.string().min(1, { message: 'First name is required' }),
    lastName: z.string().min(1, { message: 'Last name is required' }),
    role: z.string().min(1, { message: 'Role is required' }),
    username: z.string().min(3, { message: 'Username is required' }),
    email: z.string().email({ message: 'Invalid email address' }),
    confirmEmail: z.string().email({ message: 'Invalid email address' }),
    password: z.string().min(6, { message: 'Password must be at least 6 characters' })
  })
  .refine((data) => data.email === data.confirmEmail, {
    message: 'Emails do not match',
    path: ['confirmEmail']
  });

function RegisterV3() {
  const context = useContext(AppSettings);
  const [redirect, setRedirect] = useState(false);

  // Form state; note that "role" is stored as a string and "imageSrc" as a File or null.
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    username:'',
    role: '',
    email: '',
    confirmEmail: '',
    password: '',
    imageSrc: null
  });

  // Errors for field-specific validations.
  const [errors, setErrors] = useState({
    firstName: '',
    lastName: '',
    username:'',
    role: '',
    email: '',
    confirmEmail: '',
    password: ''
  });

  // Dynamic roles options for react-select.
  const [rolesOptions, setRolesOptions] = useState([]);

  useEffect(() => {
    context.handleSetAppSidebarNone(true);
    context.handleSetAppHeaderNone(true);
    context.handleSetAppContentClass('p-0');

    const fetchRoles = async () => {
      try {
        const {data:roles} = await getRoles();
        const options = roles.map((role) => ({
          value: role._id,
          label: role.name
        }));
        setRolesOptions(options);
      } catch (error) {
        toast.error('Failed to fetch roles');
        console.error('Failed to fetch roles:', error);
      }
    };

    fetchRoles();

    return () => {
      context.handleSetAppSidebarNone(false);
      context.handleSetAppHeaderNone(false);
      context.handleSetAppContentClass('');
    };
  }, [context]);

  // Handle changes for text inputs.
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
    setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  // Handle file input change for the avatar.
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setFormData((prev) => ({
      ...prev,
      imageSrc: file
    }));
  };

  // Handle changes from react-select.
  // This version uses optional chaining and nullish coalescing for brevity.
  const handleRoleChange = (selectedOption) => {
    setFormData((prev) => ({ ...prev, role: selectedOption?.value ?? '' }));
    setErrors((prev) => ({ ...prev, role: '' }));
  };

  // Submit handler: validates data, calls saveUser (passing imageSrc as second parameter),
  // and displays toast notifications.
  const handleSubmit = async (event) => {
    event.preventDefault();

    // Validate form data.
    const result = schema.safeParse(formData);
    if (!result.success) {
      const zodErrors = result.error.flatten().fieldErrors;
      setErrors({
        firstName: zodErrors.firstName ? zodErrors.firstName[0] : '',
        lastName: zodErrors.lastName ? zodErrors.lastName[0] : '',
        role: zodErrors.role ? zodErrors.role[0] : '',
        email: zodErrors.email ? zodErrors.email[0] : '',
        confirmEmail: zodErrors.confirmEmail ? zodErrors.confirmEmail[0] : '',
        password: zodErrors.password ? zodErrors.password[0] : ''
      });
      return;
    }

    try {
      const payload = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        role: formData.role,
        username:formData.username,
        email: formData.email,
        password: formData.password
      };

      // Call the service function to save the user,
      // passing the avatar (imageSrc) as the second parameter.
      await saveUser(payload, formData.imageSrc);
      toast.success('Registration successful!');
      setRedirect(true);
    } catch (error) {
      toast.error(error.message || 'Registration failed. Please try again.');
    }
  };

  if (redirect) {
    return <Navigate to="/dashboard" />;
  }

  return (
    <div className="register register-with-news-feed">
      <ToastContainer />
      <div className="news-feed">
        <div
          className="news-image"
          style={{ backgroundImage: 'url(/assets/img/login-bg/login-bg-9.jpg)' }}
        ></div>
        <div className="news-caption">
          <h4 className="caption-title">
            <b>Color</b> Admin App
          </h4>
          <p>
            As a Color Admin app administrator, you use the Color Admin console to manage your organization’s
            account, such as add new users, manage security settings, and turn on the services you want your team to access.
          </p>
        </div>
      </div>
      <div className="register-container">
        <div className="register-header mb-25px h1">
          <div className="mb-1">Sign Up</div>
          <small className="d-block fs-15px lh-16">
            Create your Color Admin Account. It’s free and always will be.
          </small>
        </div>
        <div className="register-content">
          <form onSubmit={handleSubmit} className="fs-13px">
            {/* Name Fields */}
            <div className="mb-3">
              <label className="mb-2">
                Name <span className="text-danger">*</span>
              </label>
              <div className="row gx-3">
                <div className="col-md-6 mb-2 mb-md-0">
                  <input
                    type="text"
                    className="form-control fs-13px"
                    placeholder="First name"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                  />
                  {errors.firstName && (
                    <div className="text-danger fs-12px mt-1">{errors.firstName}</div>
                  )}
                </div>
                <div className="col-md-6">
                  <input
                    type="text"
                    className="form-control fs-13px"
                    placeholder="Last name"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                  />
                  {errors.lastName && (
                    <div className="text-danger fs-12px mt-1">{errors.lastName}</div>
                  )}
                </div>
              </div>
            </div>

            {/* Role Field */}
            <div className="mb-3">
              <label className="mb-2">
                Role <span className="text-danger">*</span>
              </label>
              <Select
                options={rolesOptions}
                classNamePrefix="react-select"
                value={rolesOptions.find((option) => option.value === formData.role) || null}
                onChange={handleRoleChange}
                placeholder="Select a role"
              />
              {errors.role && (
                <div className="text-danger fs-12px mt-1">{errors.role}</div>
              )}
            </div>

            {/* Avatar Field */}
            <div className="mb-3">
              <label className="mb-2">Avatar (optional)</label>
              <input
                type="file"
                className="form-control fs-13px"
                name="avatar"
                accept="image/*"
                onChange={handleFileChange}
              />
            </div>

            <div className="mb-3">
              <label className="mb-2">
                Username <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control fs-13px"
                placeholder="Username"
                name="username"
                value={formData.username}
                onChange={handleChange}
              />
              {errors.username && (
                <div className="text-danger fs-12px mt-1">{errors.username}</div>
              )}
            </div>

            {/* Email Fields */}
            <div className="mb-3">
              <label className="mb-2">
                Email <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control fs-13px"
                placeholder="Email address"
                name="email"
                value={formData.email}
                onChange={handleChange}
              />
              {errors.email && (
                <div className="text-danger fs-12px mt-1">{errors.email}</div>
              )}
            </div>
            <div className="mb-3">
              <label className="mb-2">
                Re-enter Email <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control fs-13px"
                placeholder="Re-enter email address"
                name="confirmEmail"
                value={formData.confirmEmail}
                onChange={handleChange}
              />
              {errors.confirmEmail && (
                <div className="text-danger fs-12px mt-1">{errors.confirmEmail}</div>
              )}
            </div>

            {/* Password Field */}
            <div className="mb-4">
              <label className="mb-2">
                Password <span className="text-danger">*</span>
              </label>
              <input
                type="password"
                className="form-control fs-13px"
                placeholder="Password"
                name="password"
                value={formData.password}
                onChange={handleChange}
              />
              {errors.password && (
                <div className="text-danger fs-12px mt-1">{errors.password}</div>
              )}
            </div>

            <div className="form-check mb-4">
              <input
                className="form-check-input"
                type="checkbox"
                value=""
                id="agreementCheckbox"
              />
              <label className="form-check-label" htmlFor="agreementCheckbox">
                By clicking Sign Up, you agree to our{' '}
                <Link to="/user/register">Terms</Link> and that you have read our{' '}
                <Link to="/user/register">Data Policy</Link>, including our{' '}
                <Link to="/user/register">Cookie Use</Link>.
              </label>
            </div>
            <div className="mb-4">
              <button
                type="submit"
                className="btn btn-theme d-block w-100 btn-lg h-45px fs-13px"
              >
                Sign Up
              </button>
            </div>
            <div className="mb-4 pb-5">
              Already a member? Click <Link to="/login">here</Link> to login.
            </div>
            <hr className="bg-gray-600 opacity-2" />
            <p className="text-center text-gray-600">
              &copy; Color Admin All Right Reserved 2025
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}

export default RegisterV3;
